<template>
  <div class="left-column">
    <form @submit.prevent>
      <!-- Header region here -->
      <div class="your-profile-header">
        <span class="caption editorial"> Profile</span>
      </div>

      <!-- First Name here -->
      <div class="form-group">
        <label for="first-name">First Name </label>
        <input
          id="first-name"
          placeholder="First name"
          class="form-control"
          v-model="user.firstName"
          readonly
        />
      </div>

      <!-- last Name -->
      <div class="form-group">
        <label for="last-name">Last Name </label>
        <input
          id="last-name"
          placeholder="Last name"
          class="form-control"
          v-model="user.lastName"
          readonly
        />
      </div>

      <!-- Email Here -->
      <div class="form-group">
        <label for="email">Email </label>
        <input
          id="email"
          type="email"
          placeholder="Email"
          class="form-control"
          :value="user.email"
          disabled
        />
      </div>

      <!-- Password Here -->
      <div class="form-group" :class="{ password: editUserProfile }">
        <label for="password">Password </label>
        <template v-if="!editUserProfile">
          <input
            id="password"
            placeholder="New Password"
            type="password"
            class="form-control"
            value="**********"
            readonly
          />

          <button
            class="change-password"
            @click="editUserProfile = !editUserProfile"
          >
            change password
          </button>
        </template>

        <template v-else>
          <div class="change-password-region">
            <div class="absolute flex justify-end close-btn">
              <p
                class="ml-auto editorial cursor-pointer"
                @click="editUserProfile = !editUserProfile"
              >
                x
              </p>
            </div>

            <p class="ml-auto inter  label">
              Type your current password
            </p>
            <div class="password-holder">
              <input
                id="password"
                placeholder="Enter current password"
                :type="showCurrentPassword ? 'text' : 'password'"
                class="form-control"
                v-model="currentPassword"
              />

              <img
                src="../../assets/lashes.svg"
                alt="eyelid"
                class="cursor-pointer"
                :class="{ rotate: showCurrentPassword }"
                @click="showCurrentPassword = !showCurrentPassword"
              />
            </div>

            <div class="validations">
              <p class="ml-auto inter  label">
                Your new password must contain at least:
              </p>
              <div class="validation">
                <img
                  src="../../assets/checkmark.svg"
                  :class="[
                    $v.password.containsUppercase ? 'opacity-1' : 'opacity-0'
                  ]"
                  alt=""
                />
                <span>1 uppercase letter (A-Z)</span>
              </div>
              <div class="validation">
                <img
                  :class="[
                    $v.password.containsLowercase ? 'opacity-1' : 'opacity-0'
                  ]"
                  src="../../assets/checkmark.svg"
                  alt=""
                />
                <span>1 lowercase letter (a-z)</span>
              </div>
              <div class="validation">
                <img
                  :class="[
                    $v.password.containsNumber ? 'opacity-1' : 'opacity-0'
                  ]"
                  src="../../assets/checkmark.svg"
                  alt=""
                />
                <span>1 number (0-9)</span>
              </div>
              <div class="validation">
                <img
                  :class="[
                    $v.password.containsSpecial ? 'opacity-1' : 'opacity-0'
                  ]"
                  src="../../assets/checkmark.svg"
                  alt=""
                />
                <span>1 special character (!@#$%^&*)</span>
              </div>
            </div>

            <div class="password-holder">
              <input
                id="password"
                placeholder="Enter new password"
                :type="showPassword ? 'text' : 'password'"
                class="form-control"
                v-model="password"
              />

              <img
                src="../../assets/lashes.svg"
                alt="eyelid"
                class="cursor-pointer"
                :class="{ rotate: showPassword }"
                @click="showPassword = !showPassword"
              />
            </div>

            <div class="password-holder">
              <input
                id="password"
                placeholder="Re-enter new password"
                type="password"
                class="form-control"
                :class="{ 'error-border': !$v.reTypePassword.sameAs }"
                v-model="reTypePassword"
              />

              <p class="error" v-if="!$v.reTypePassword.sameAs">
                Passwords do not match. Please try again.
              </p>
            </div>

            <button
              class="button-gemnote button-gemnote--small"
              :class="{ 'button-gemnote--disabled': disabledButton }"
              :disabled="disabledButton"
              @click="updateUserProfile"
            >
              <span>Save</span>
            </button>
          </div>
        </template>
      </div>
    </form>

    <!-- Advanced region here -->
    <div class="your-profile-sso">
      <div class="flex items-center gap-1">
        <span class="font-semibold title">
          Single Sign-On is turned {{ isIDPEnabled ? 'ON' : 'OFF' }}
        </span>
        <!-- <img src="../../assets//success-check.svg" alt="" v-if="isIDPEnabled" /> -->
        <img src="../../assets/warning.svg" v-if="!isIDPEnabled" />
      </div>
      <div class="flex items-center gap-1 advance-info">
        <span class="font-light">To make changes go to </span>
        <button
          @click="$router.push('/profile/advanced')"
          class="font-bold underline"
        >
          Advanced Settings.
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { required, sameAs } from 'vuelidate/lib/validators'
import { mapState } from 'vuex'
import { status } from '../../helpers'

export default {
  name: 'ChangePasswordForm',
  data() {
    return {
      showCurrentPassword: false,
      showPassword: false,
      editUserProfile: false,
      currentPassword: '',
      password: '',
      reTypePassword: ''
    }
  },

  validations: {
    currentPassword: {
      required
    },
    password: {
      required,
      containsUppercase: value => {
        return /[A-Z]/.test(value)
      },
      containsLowercase: value => {
        return /[a-z]/.test(value)
      },
      containsNumber: value => {
        return /[0-9]/.test(value)
      },
      containsSpecial: value => {
        return /[#?!@$%^&*-]/.test(value)
      }
    },

    reTypePassword: {
      required,
      sameAs: sameAs(vm => {
        return vm.password
      })
    }
  },

  computed: {
    ...mapState({
      user: state => state.profile.user,
      changingPassword: state => state.auth.changingPassword,
      updatigUserProfile: state => state.profile.updatigUserProfile,
      IDPProfile: state => state.profile.IDPProfile,
      isIDPEnabled: state => state.profile.IDPProfile.enabled
    }),
    changePasswordSucceed() {
      if (this.changePassword.success) {
        this.cancelChange()
        return this.changePassword
      } else {
        return this.changePassword
      }
    },

    disabledButton() {
      return (
        this.updatigUserProfile ||
        this.$v.$invalid ||
        !this.password ||
        !this.reTypePassword ||
        !this.$v.reTypePassword.sameAs ||
        !this.$v.currentPassword
      )
    }
  },
  methods: {
    startProfileEdition() {
      this.editUserProfile = true
      this.password = ''
      this.reTypePassword = ''
    },
    cancelProfileEdition() {
      this.editUserProfile = false
      this.password = '**********'
      this.reTypePassword = ''
    },
    async updateUserProfile() {
      console.log(this.$v)

      await this.$v.$touch()
      const isValid = this.$v.$invalid
      if (isValid) {
        return
      } else {
        this.$store.dispatch('auth/changePassword', {
          password: this.password,
          reTypePassword: this.reTypePassword
        })
        this.editUserProfile = false
      }
    }
  },
  watch: {
    changingPassword(value) {
      if (value === status.ERROR) {
        return
      } else {
        // this.$store.dispatch('profile/updateUserProfile', {
        //   hideNotification: false
        // })
        // this.cancelProfileEdition()
      }
    }
  }
}
</script>

<style scoped lang="scss">
@import '@/global.scss';

.left-column {
  padding: 2.5rem 3.5rem;
  background: inherit;
  border: 1px solid $onyx;
  border-radius: 29px;
  margin-bottom: 2rem;

  .your-profile-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 2rem;

    .ml-auto {
      .button-gemnote-glass--tiny {
        font-weight: normal;
      }
    }

    .caption {
      text-align: left;
      font-size: 1.1rem;
      font-weight: normal;
    }
  }
}

.form-group {
  color: $main-6;
  display: flex;
  align-items: center;
  margin-bottom: 1.75rem;

  &.password {
    align-items: start;
  }

  input {
    font-size: 0.813rem;
    height: 1.5rem;
    width: 35%;

    &:disabled {
      border-bottom: none;
    }

    &::placeholder {
      font-size: 0.813rem;
    }

    &[type='email'] {
      color: #a09c95;
      opacity: 0.8;
      border-bottom: 1px solid #232220;

      &.underline {
        text-decoration: underline;
        border-bottom: none;
      }
    }

    &[type='password'] {
      width: 35%;
      color: #a09c95;

      &:disabled {
        color: $gem-cold-dark-gray;
      }
    }
  }

  label {
    text-align: left;
    font-size: 0.813rem;
    font-weight: bold;
    margin-bottom: 0px !important;
    line-height: 19px;
    width: 18%;
  }
}

.button-gemnote--small {
  height: 30px;
  width: auto;
  padding: 0 20px !important;
  font-size: 0.7rem;
  margin-top: 0.75rem;
}

.password-actions {
  ul {
    list-style-type: none;
    display: flex;
    align-items: center;
  }

  ul li {
    padding-right: 1rem;
    font-weight: 700;
    font-size: 18px;
    text-decoration: underline;
    cursor: pointer;
  }
}

.form-button {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  justify-content: center;
  padding-top: 1rem;

  button {
    width: 5.938rem !important;
    padding: 0 1rem;
    height: 2.5rem;
  }
}

.change-password {
  background: #dfdad2;
  border: 1px solid #232220;
  border-radius: 76px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #232220;
  font-size: 0.625rem;
  padding: 0 0.75rem;
  margin-left: 1rem;
  text-transform: uppercase;
}

.change-password-region {
  position: relative;
  border: 1px solid #232220;
  padding: 1.5rem 2rem;
  text-align: left;
  width: 50%;
  transition: width 2s, height 4s;
  transition-timing-function: ease-in-out;

  .close-btn {
    right: 20px;
    top: 10px;
  }

  .label {
    font-size: 0.793rem;
    font-weight: 700;
  }

  .validations {
    margin: 1rem 0 2rem 0;

    .label {
      margin-bottom: 1rem;
    }
    .validation {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 0.753rem;
      margin-bottom: 0.5rem;
    }
  }

  .password-holder {
    padding-bottom: 12px;
    margin-top: 12px;
    width: 80%;
    position: relative;

    input {
      background: inherit;

      &[type='password'] {
        width: 100%;
        color: #232220;
      }

      &.error-border {
        border-bottom: 1px solid #ce3c3c !important;
      }

      &::placeholder {
        font-size: 0.75rem;
        color: #a09c95;
        font-weight: normal;
      }
    }

    img {
      position: absolute;
      right: 10px;
      top: 15px;
      transition: transform 0.8s;

      &.rotate {
        transform: rotateX(180deg);
      }
    }

    .error {
      color: #ce3c3c;
      font-size: 0.625rem;
      font-weight: 700;
      font-style: normal;
      padding-top: 5px;
    }
  }
}

@media (max-width: 575.98px) {
  .left-column {
    padding: 2rem;
  }
  .form-button {
    flex-wrap: wrap;
    .whitebutton-gemnote--medium {
      width: 100%;
    }
    .button-gemnote--medium {
      width: 100%;
    }
  }
  .password-actions {
    ul li button {
      width: auto;
    }
  }
}

@media (min-width: 576px) and (max-width: 767.98px) {
  .form-button {
    flex-wrap: wrap;
    .whitebutton-gemnote--medium {
      width: 100%;
    }
    .button-gemnote--medium {
      width: 100%;
    }
  }
  .password-actions {
    ul li button {
      width: auto;
    }
  }
}

@media (min-width: 1099px) and (max-width: 1480px) {
  .form-button {
    flex-wrap: wrap;
    .whitebutton-gemnote--medium {
      width: 100%;
    }
    .button-gemnote--medium {
      width: 100%;
    }
  }
  .password-actions {
    ul li button {
      width: auto;
    }
  }
}

@media (max-width: 768px) {
  .change-password-region {
    width: 100%;
  }
}

input:-webkit-autofill,
input:-webkit-autofill:hover,
input:-webkit-autofill:focus,
input:-webkit-autofill:active {
  -webkit-box-shadow: inherit !important;
}

.your-profile-sso {
  margin-top: 2rem;
  display: flex;
  gap: 0.1rem;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  color: #24292c;

  .title {
    font-size: 0.913rem;
  }

  .advance-info {
    font-size: 0.813rem;
  }
}
</style>
